import { Area } from 'recharts';
import { chartColor, type ChartColorKey } from '@/lib/chart-colors';
import {
  PARTIAL_DASH,
  partialDot,
  type PartialLabels,
} from '@/components/ui/charts/chart-partial';

export type MetricsChartAreaSeriesProps = {
  dataKey: string;
  name?: string;
  colorKey?: ChartColorKey;
  strokeWidth?: number;
  stackId?: string;
  /**
   * Key of the dashed segment produced by `markPartialRows` (`${dataKey}Partial`);
   * draws the partial buckets dashed with a hollow ring (CHART-02).
   */
  partialDataKey?: string;
  /** Labels next to the first/last partial point (CHART-03). */
  partialLabels?: PartialLabels;
};

/**
 * Standard area series: chart color stroke, gradient fill, 1px line by default.
 */
export function MetricsChartAreaSeries({
  dataKey,
  name,
  colorKey = 1,
  stackId = '1',
  partialDataKey,
  partialLabels = {},
}: MetricsChartAreaSeriesProps) {
  const color = chartColor(colorKey);
  return (
    <>
      <Area
        type="linear"
        dataKey={dataKey}
        name={name}
        stackId={stackId}
        stroke={color}
        strokeWidth={2}
        fill={'hsl(var(--chart-4))'}
        fillOpacity={0}
      />
      {partialDataKey ? (
        // Not stacked: it repeats the solid series' values on its own buckets.
        <Area
          type="linear"
          dataKey={partialDataKey}
          name={name}
          stroke={color}
          strokeWidth={2}
          strokeDasharray={PARTIAL_DASH}
          fillOpacity={0}
          connectNulls={false}
          legendType="none"
          dot={partialDot(color, partialLabels)}
        />
      ) : null}
    </>
  );
}

export type MetricsChartLineSeriesProps = {
  dataKey: string;
  name?: string;
  color: string;
  /** SVG dash (`undefined` = solid). */
  dash?: string;
};

/**
 * Secondary line without fill, stack or partial segment: the previous period
 * (gray, dashed) or the site ticket (gray) of the Vendas charts (ANLT-04).
 */
export function MetricsChartLineSeries({
  dataKey,
  name,
  color,
  dash,
}: MetricsChartLineSeriesProps) {
  return (
    <Area
      type="linear"
      dataKey={dataKey}
      name={name}
      stroke={color}
      strokeWidth={1.5}
      strokeDasharray={dash}
      fill="none"
      fillOpacity={0}
      connectNulls={false}
      dot={false}
      activeDot={false}
      legendType="none"
    />
  );
}
